import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, signal } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25Util } from "../../../util/s25-util";
import { S25EditableModule } from "../../s25-editable/s25.editable.module";
import { Item } from "../../../pojo/Item";
import { CustomAttributes } from "../../../pojo/CustomAttributes";
import { OrganizationProfileApi } from "../../../services/organization/organization.profile.api";
import { Bind } from "../../../decorators/bind.decorator";
import { SeEditableLogo } from "../se.editable.logo.component";
import { Attribute } from "../../s25-event/EventMicroI";
import type { S25WsMicroService } from "../../../services/MicroServices/S25WsMicroService";
import { SeSecurityStore } from "../se.security.store";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { StudentEngagementOrg } from "../StudentEngagementOrg";
import { SeOrgDetailsStore } from "./se.org.details.store";
import { S25FavoriteButtonComponent } from "../../../standalone/s25-favorite-button/s25.favorite.button.component";
import { LoginStore } from "../../../stores/login.store";
import { SeOrgSecurityStore } from "../se.org.security.store";
import { SeOrgStatusService } from "../se.org.status.service";

@TypeManagerDecorator("se-about-org")
@Component({
    selector: "se-about-org",
    template: `
        <se-editable-logo
            class="org-image-container"
            [itemId]="orgId()"
            [itemTypeId]="orgTypeId"
            [imageAttr]="imageAttr()"
            [canEdit]="canEdit()"
        ></se-editable-logo>
        @if (errorMsg()) {
            <p class="ngRed">{{ errorMsg() }}</p>
        }
        <div class="org-content">
            @if (loginStore.loggedIn()) {
                <s25-ng-favorite-button
                    class="float-end"
                    [itemTypeId]="Item.Ids.Organization"
                    [itemId]="orgId()"
                    [display]="'bookmark'"
                />
            }

            @if (orgRating()?.itemName || canChangeOrgRating()) {
                <div class="rating-section float-end">
                    <label class="c-label ngBold">Status</label>
                    @if (canChangeOrgRating()) {
                        <s25-ng-dropdown-search-criteria
                            [type]="'organizationRatings'"
                            [(value)]="ratingInput"
                            [ariaLabel]="'Status'"
                            (valueChange)="updateRating()"
                        />
                    } @else {
                        <p class="c-margin-left--half c-pill-badge {{ ratingColor() }}">
                            {{ orgRating()?.itemName }}
                        </p>
                    }
                </div>
            }

            @if (mission() || canEdit()) {
                <div class="mission-section">
                    <h3>Our Mission</h3>
                    <s25-ng-editable-textarea
                        [val]="missionInput()"
                        [readOnly]="!canEdit()"
                        [hasCommit]="true"
                        [onCommit]="updateMissionStatement"
                        [renderHtml]="true"
                        (valChange)="missionInput.set($event)"
                    ></s25-ng-editable-textarea>
                </div>
            }

            @if (description()) {
                <div class="description-section">
                    <h3>About Us</h3>
                    <p class="description-text">{{ description() }}</p>
                </div>
            }

            @if (website()) {
                <div class="website-section">
                    <h3>Website</h3>
                    <p class="website-text">
                        <a [href]="website()" target="_blank" rel="noopener noreferrer">{{ website() }}</a>
                    </p>
                </div>
            }
        </div>
    `,
    styles: `
        :host {
            display: flex;
            gap: 2rem;
            align-items: flex-start;
        }

        .org-content {
            flex: 1;
            text-align: left;
        }

        .org-content h3 {
            color: var(--color--text);
            font-size: 1.3rem;
            margin: 0 0 0.75rem 0;
            font-weight: 600;
        }

        .mission-section,
        .description-section,
        .website-section {
            margin-bottom: 1.5rem;
        }

        .description-text,
        .website-text {
            color: var(--color--text-muted);
            line-height: 1.6;
            margin: 0;
        }

        .website-text a {
            color: var(--cn-blue);
            text-decoration: none;
            font-weight: 500;
        }

        .website-text a:hover {
            text-decoration: underline;
        }

        ::ng-deep .mission-section s25-ng-editable-textarea textarea {
            width: 100%;
        }

        ::ng-deep .mission-section s25-ng-editable-textarea .aw-button-group {
            width: fit-content;
            margin-left: auto;
        }

        ::ng-deep .mission-section s25-ng-editable-textarea .aw-button--outline {
            margin-right: 10px;
        }

        ::ng-deep .form-content s25-ng-editable-text .c-input {
            width: 100%;
        }

        .rating-section {
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }

        .rating-section s25-ng-dropdown-search-criteria {
            margin-left: 0.5rem;
        }

        s25-ng-favorite-button {
            margin-left: 1rem;
            font-size: 1.25rem;
        }

        @media (max-width: 768px) {
            :host {
                flex-direction: column;
                gap: 1.5rem;
                align-items: center;
            }

            .org-content {
                text-align: center;
                width: 100%;
            }

            .org-content h3 {
                font-size: 1.1rem;
            }

            .mission-section,
            .description-section,
            .website-section {
                margin-bottom: 1rem;
            }

            .rating-section {
                justify-content: center;
                flex-wrap: wrap;
                float: none !important;
                margin-bottom: 1rem;
            }

            .rating-section s25-ng-dropdown-search-criteria {
                margin-left: 0;
            }
        }

        @media (max-width: 480px) {
            :host .mission-section s25-ng-editable-textarea ::ng-deep .aw-button-group {
                width: 100%;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [S25EditableModule, SeEditableLogo, S25DropdownPaginatedModule, S25FavoriteButtonComponent],
})
export class SeAboutOrgComponent {
    readonly orgId = input.required<number>();
    readonly mission = input<string>();
    readonly imageAttr = input<Attribute | S25WsMicroService.ItemAttribute>();
    readonly description = input<string>();
    readonly website = input<string>();
    readonly orgRating = input<StudentEngagementOrg.OrgRating | undefined>();
    readonly isEditable = input<boolean | undefined>();

    protected readonly securityStore = inject(SeSecurityStore);
    protected readonly loginStore = inject(LoginStore);
    orgSecStore = inject(SeOrgSecurityStore);

    private readonly orgDetailsStore = inject(SeOrgDetailsStore);

    protected canEdit = computed(() => {
        const editableOverride = this.isEditable();
        if (editableOverride !== undefined) {
            return editableOverride;
        }
        return this.orgSecStore.canEditOrg();
    });

    // Rating edits require Student Engagement Full and CU_ACCT_RATING Full.
    protected canChangeOrgRating = computed(() => this.securityStore.canEditOrgRating());

    protected ratingColor = computed(() => {
        const effects = this.orgSecStore.statusEffects();
        if (effects.some((effect) => effect.mode === "DENY")) {
            return "c-state-badge--red";
        }
        if (effects.some((effect) => effect.mode === "WARN")) {
            return "c-state-badge--yellow";
        }
        if (this.ratingInput()) {
            return "c-state-badge--green";
        }
        return undefined;
    });

    orgTypeId = Item.Ids.Organization;

    missionAttrId = CustomAttributes.system.StudentEngagementOrgMission.id;
    missionInput = linkedSignal(() => this.mission());
    ratingInput = linkedSignal(() => {
        return this.orgRating();
    });
    errorMsg = signal<string>("");

    @Bind
    async updateMissionStatement() {
        const mission = this.missionInput() ?? "";
        const [_, error] = await S25Util.Maybe(
            OrganizationProfileApi.saveProfile(this.orgId(), { updateMission: true, mission }),
        );
        if (error) {
            this.errorMsg.set(S25Util.errorText(error, ""));
            console.error(error);
            return;
        }
        this.errorMsg.set("");
        this.orgDetailsStore.patchDetails({ mission });
    }

    @Bind
    async updateRating() {
        const rating = this.ratingInput();
        if (!rating?.itemId) {
            return;
        }

        const [_, error] = await S25Util.Maybe(OrganizationProfileApi.saveRating([this.orgId()], +rating.itemId));
        if (error) {
            this.errorMsg.set(S25Util.errorText(error, ""));
            console.error(error);
            return;
        }

        this.errorMsg.set("");
        const statusPayload = await SeOrgStatusService.resolveEffects(this.orgId());
        this.orgDetailsStore.patchDetails({
            orgRating: {
                itemId: rating.itemId,
                itemName: rating.itemName,
            },
            statusEffects: statusPayload.statusEffects ?? [],
            statusMessages: statusPayload.statusMessages ?? [],
        });
    }

    protected readonly Item = Item;
}
